import { useCallback, useMemo, useState } from "react";
import { formPhotoDeleteMessage } from "#app/components/forms/form-fill/delete-submission";
import { useRemoveFormPhotos } from "#app/lib/forms";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTable,
  DataTableEmpty,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  DataTableSortByDropdown,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { ViewModeToggle } from "#app/components/view-options/view-mode-toggle";
import { MemberQuickFiltersDivider } from "#app/components/members/member-quick-filters";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useIssueAttachments } from "#app/lib/hooks/use-issue-attachments";
import { removeInlineMediaFromHtml } from "#app/lib/media-content";
import { mediaKind, type MediaKind } from "#app/lib/media-kind";
import { isRichTextEmpty } from "#app/lib/utils/rich-text-value";
import type { ActivityContextValue, AttachmentsFocus } from "./activity/activity-context";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useMediaDownloads } from "#app/lib/hooks/use-media-downloads";
import { countOf } from "#app/lib/utils/pluralize";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import { IssueMediaGrid } from "./issue-media-grid";
import {
  MEDIA_GROUP_BY_LABELS,
  MEDIA_SOURCES,
  mediaItemOrigin,
  type MediaGroupBy,
  type MediaRow,
  type MediaSource,
  useIssueMediaColumns,
  useIssueMediaGroups,
  useIssueMediaRows,
  useMediaNavigate,
  renamable,
} from "./issue-media-columns";
import type { BulkAction, OnChangeFn, Omittable, SortingState, VisibilityState } from "@salestrak/ui";

type NoteItem = Extract<IssueMediaItem, { source: "note" | "note_inline" }>;

const ALL = "all";
const FOCUS = "focus";

const MEDIA_KINDS: readonly (readonly [MediaKind, string])[] = [
  ["image", "Photos"],
  ["video", "Videos"],
  ["pdf", "PDFs"],
  ["file", "Documents"],
];
const kindOf = (item: IssueMediaItem): MediaKind => mediaKind(item.contentType ?? "image/*");

function mediaPlace(item: IssueMediaItem): { id: string; label: string } | null {
  if (item.source === "form") return { id: `form:${item.onFile.form.formId}`, label: item.onFile.form.name };
  if (item.source === "field") return { id: `field:${item.field.customFieldId}`, label: item.field.name };
  return null;
}

const DEFAULT_GROUP_BY: MediaGroupBy = "all";

const MEDIA_SORT_LABELS: Record<string, string> = {
  none: "None",
  date: "Added",
  name: "Name",
  location: "From",
};
const MEDIA_SORT_ICONS = { date: "calendar", name: "a-large-small", location: "inbox" } as const;

// Pins replace the default ["select"], so name it too.
const PINNED_COLUMN_IDS = ["select", "name"];

const NO_KINDS: ReadonlySet<MediaKind> = new Set();

const rowId = (row: MediaRow) => `${row.source}:${row.photoId}`;

type IssueMediaTabProps = Omittable<{
  items: IssueMediaItem[];
  /** The owning issue — keys the table's scroll window. */
  issueId: string;
  /** Prefixes download filenames / the zip name. */
  issueName?: string;
  canEdit?: boolean;
  /** Quick links — jump to the field row on the summary tab… */
  onNavigateToField: (customFieldId: string) => void;
  /** …or to the note on the activity tab. */
  onNavigateToNote: (noteId: string, parentNoteId?: string | null) => void;
  notes: Pick<ActivityContextValue, "updateNote" | "deleteNote">;
  focus?: AttachmentsFocus | null;
  onClearFocus?: () => void;
}>;

/**
 * Media tab: every file on the issue (direct uploads, custom fields, note
 * attachments, inline note images) in one table, with per-row source, an
 * optional group-by, quick-link navigation to where the file lives,
 * single/bulk download, and — with edit access — Upload, plus Remove on
 * direct uploads and field photos, and — for whoever may edit the note — on
 * a note's photos. Remove takes the photo out of where it lives. The row,
 * column, and group builders live in `issue-media-columns.tsx`, shared with
 * the attachments widget.
 */
export function IssueMediaTab({
  items,
  issueId,
  issueName,
  canEdit = false,
  onNavigateToField,
  onNavigateToNote,
  notes,
  focus,
  onClearFocus,
}: IssueMediaTabProps) {
  const { state: view, update: updateView } = usePersistedState<{
    groupBy: MediaGroupBy;
    mode: "list" | "grid";
    sorting: SortingState;
    columnVisibility: VisibilityState;
  }>("attachments-tab-view-v3", { groupBy: DEFAULT_GROUP_BY, mode: "list", sorting: [], columnVisibility: {} });
  const setSorting: OnChangeFn<SortingState> = (next) =>
    updateView((current) => ({ sorting: typeof next === "function" ? next(current.sorting) : next }));
  const sort = view.sorting[0];
  const [sourceFilter, setSourceFilter] = useState<MediaSource | null>(null);
  const [fromFilter, setFromFilter] = useState<string | null>(null);
  const [pickedTypes, setTypes] = useState<ReadonlySet<MediaKind>>(new Set());
  const types = useMemo(() => {
    const present = new Set(items.map(kindOf));
    return present.size < 2 ? NO_KINDS : new Set([...pickedTypes].filter((kind) => present.has(kind)));
  }, [items, pickedTypes]);
  const narrowed = useMemo(() => {
    const focused = focus ? new Set(focus.ids) : null;
    return items.filter((item) =>
      focused
        ? focused.has(item.photoId)
        : (sourceFilter == null || mediaItemOrigin(item) === sourceFilter) &&
          (fromFilter == null || mediaPlace(item)?.id === fromFilter),
    );
  }, [focus, fromFilter, items, sourceFilter]);
  const shown = useMemo(
    () => (types.size === 0 ? narrowed : narrowed.filter((item) => types.has(kindOf(item)))),
    [narrowed, types],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const navigate = useMediaNavigate({ onOpenLightbox: setLightboxIndex, onNavigateToField, onNavigateToNote });
  const fromLink = useCallback(
    (row: MediaRow) => (row.item.source === "upload" ? undefined : () => navigate(row)),
    [navigate],
  );
  const { rows, lightboxMedia } = useIssueMediaRows({
    items: shown,
    sorting: view.sorting,
    fromLink,
  });

  const { downloadPhoto, downloadAllPhotos, downloadingAll } =
    useMediaDownloads(issueName);

  const { upload, uploading, remove, removing: removeBusy, rename } = useIssueAttachments(issueId);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const renameRow = (row: MediaRow, name: string) => {
    if (name.trim() === row.name) return;
    void rename(row.photoId, name);
  };
  const columns = useIssueMediaColumns({
    onOpenLightbox: setLightboxIndex,
    onNavigateToField,
    onNavigateToNote,
    renamingId,
    onRename: renameRow,
    onRenameEnd: () => setRenamingId(null),
    tileSize: 40,
  });
  const groups = useIssueMediaGroups(rows, issueId, view.groupBy);
  const emptyMessage = items.length > 0 ? "No attachments match these filters" : "No attachments on this Project";
  const filterKey = `${sourceFilter ?? ALL}:${fromFilter ?? ALL}:${[...types].join()}:${focus?.label ?? ""}`;

  const [removing, setRemoving] = useState<MediaRow | null>(null);
  const { removePhotoFieldValue } = useIssueViewMutations();
  const removeFormPhotos = useRemoveFormPhotos();
  const removeRows = async (targets: MediaRow[]) => {
    // One write for all chosen photos; one per photo would put back the photo the previous write took out.
    const fromNotes = new Map<string, { note: NoteItem["note"]; ids: Set<string> }>();
    let failed = 0;
    const fromForms: string[] = [];
    for (const { item } of targets) {
      if (item.source === "form") {
        fromForms.push(item.photoId);
      } else if (item.source === "upload") {
        if (!(await remove(item.photoId))) failed += 1;
      } else if (item.source === "field") {
        if (!(await removePhotoFieldValue(issueId, item.field.customFieldId, item.photoId))) failed += 1;
      } else {
        const entry = fromNotes.get(item.note.noteId) ?? { note: item.note, ids: new Set<string>() };
        entry.ids.add(item.photoId);
        fromNotes.set(item.note.noteId, entry);
      }
    }
    for (const { note, ids } of fromNotes.values()) {
      const html = [...ids].reduce(removeInlineMediaFromHtml, note.note ?? "");
      const mediaIds = (note.media ?? []).map((media) => media.id).filter((id) => !ids.has(id));
      const written =
        isRichTextEmpty(html) && mediaIds.length === 0 && note.viewerCanDelete && !note.replies?.length
          ? await notes.deleteNote(note.noteId)
          : await notes.updateNote(note.noteId, html, mediaIds);
      if (!written) failed += ids.size;
    }
    if (fromForms.length > 0) failed += await removeFormPhotos(issueId, fromForms);
    if (failed > 0) throw new Error(`${countOf(failed, "file")} couldn't be deleted`);
  };
  const confirmRemove = async () => {
    if (!removing) return;
    await removeRows([removing]).catch(() => undefined);
    setRemoving(null);
  };
  const isMobile = useIsMobile();
  const bulkActions: BulkAction<MediaRow>[] = [
    {
      id: "download",
      type: "direct",
      label: "Download",
      icon: "download",
      quiet: true,
      onAction: (selected) =>
        downloadAllPhotos(
          selected.map((row) => ({ id: row.photoId, fileName: row.item.fileName })),
          "media",
        ),
    },
    {
      id: "remove",
      type: "confirm",
      label: "Delete",
      icon: "trash",
      destructive: true,
      hidden: (selected) => !selected.every((row) => removable(row.item, canEdit)),
      confirmTitle: (selected) => `Delete ${countOf(selected.length, "file")}?`,
      confirmDescription:
        "Each is deleted from where it lives, its field, its comment or this Project, for everyone. A form's photos also leave their submission.",
      confirmLabel: "Delete",
      successMessage: (_, count) => `Deleted ${countOf(count, "file")}`,
      onAction: removeRows,
    },
  ];
  const shownBulkActions = isMobile ? [] : bulkActions;

  const rowMenu = (row: MediaRow) => (
    <>
      <MenuItem onClick={() => downloadPhoto(row.photoId, row.index + 1, row.item.fileName)}>
        <Icon name="download" />
        Download
      </MenuItem>
      {canEdit && renamable(row.item) ? (
        <MenuItem onClick={() => setRenamingId(row.photoId)}>
          <Icon name="pencil" />
          Rename
        </MenuItem>
      ) : null}
      {removable(row.item, canEdit) ? (
        <>
          <MenuSeparator />
          <MenuItem variant="destructive" onClick={() => setRemoving(row)}>
            <Icon name="trash" />
            Delete
          </MenuItem>
        </>
      ) : null}
    </>
  );

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <DataTableToolbar>
        <DataTableToolbarLeft>
          {/* The tab's primary act leads the row, filled, ahead of the
              chips past a hairline. */}
          {canEdit ? (
            <>
              {/* Its glyph alone on a phone, where the row has no room to
                  spare; the fill still marks it the tab's one act. */}
              <Button
                size={isMobile ? "icon-xs" : "xs"}
                aria-label={isMobile ? "Upload" : undefined}
                loading={uploading > 0}
                onClick={() => void upload()}
              >
                <Icon name="upload" size="xs" />
                {isMobile ? null : "Upload"}
              </Button>
              <MemberQuickFiltersDivider />
            </>
          ) : null}
          <MediaGroupChip
            value={view.groupBy}
            onChange={(groupBy) => updateView({ groupBy })}
          />
          <DataTableSortByDropdown
            sortByField={sort?.id ?? "none"}
            setSortByField={(field) => setSorting(field === "none" ? [] : [{ id: field, desc: sort?.desc ?? false }])}
            sortByLabels={MEDIA_SORT_LABELS}
            sortByIcons={MEDIA_SORT_ICONS}
            sortDirection={sort?.desc ? "desc" : "asc"}
            setSortDirection={(direction) => {
              if (sort) setSorting([{ id: sort.id, desc: direction === "desc" }]);
            }}
          />
        </DataTableToolbarLeft>
        <DataTableToolbarActions>
          <MediaFilterMenu
            items={items}
            source={sourceFilter}
            from={fromFilter}
            focusLabel={focus?.label}
            onSource={(next) => {
              onClearFocus?.();
              setSourceFilter(next);
            }}
            onFrom={(next) => {
              onClearFocus?.();
              setFromFilter(next);
            }}
            onClear={() => {
              onClearFocus?.();
              setSourceFilter(null);
              setFromFilter(null);
              setTypes(new Set());
            }}
          />
          <MediaTypePills items={items} narrowed={narrowed} value={types} onChange={setTypes} />
          {/* A tool, not the tab's purpose: a quiet glyph beside the view
              toggle, so Upload stays the one filled control on the row. The
              toggle holds the row's end. */}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Download all"
                  disabled={items.length === 0}
                  loading={downloadingAll}
                  onClick={() => downloadAllPhotos(lightboxMedia, "media")}
                />
              }
            >
              <Icon name="download" />
            </TooltipTrigger>
            <TooltipPopup side="bottom">Download all</TooltipPopup>
          </Tooltip>
          <MediaViewToggle mode={view.mode} onModeChange={(mode) => updateView({ mode })} />
        </DataTableToolbarActions>
      </DataTableToolbar>
      {view.mode === "grid" ? (
        <IssueMediaGrid
          key={`${filterKey}:${view.groupBy}`}
          rows={rows}
          groupBy={view.groupBy}
          rowId={rowId}
          bulkActions={shownBulkActions}
          onOpen={setLightboxIndex}
          renderMenu={rowMenu}
          renamingId={renamingId}
          onRename={renameRow}
          onRenameEnd={() => setRenamingId(null)}
          emptyMessage={emptyMessage}
        />
      ) : (
        // The gap sits outside the scroller, not as `gutterTop`: that let the pinned Attachments band slide up before it stuck.
        <Box direction="column" fill="flex" paddingTop="sm">
          <DataTable
            key={filterKey}
            columns={columns}
            groups={groups}
            getRowId={rowId}
            pinnedColumnIds={PINNED_COLUMN_IDS}
            sorting={view.sorting}
            onSortingChange={setSorting}
            columnVisibility={view.columnVisibility}
            onColumnVisibilityChange={(columnVisibility) => updateView({ columnVisibility })}
            bulkActions={shownBulkActions}
            density="comfortable"
            stickyHeader
            paddingX="lg"
            renderRowActions={rowMenu}
            emptyState={<DataTableEmpty message={emptyMessage} />}
          />
        </Box>
      )}
      <MediaLightbox
        media={lightboxMedia}
        open={lightboxIndex != null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
        initialIndex={lightboxIndex ?? 0}
        title="Attachments"
        downloadPrefix={issueName}
        onRemove={(_, index) => setRemoving(rows[index] ?? null)}
        canRemove={(index) => !!rows[index] && removable(rows[index].item, canEdit)}
        onRename={(index, name) => rows[index] && renameRow(rows[index], name)}
        canRename={(index) => canEdit && !!rows[index] && renamable(rows[index].item)}
      />
      <AlertDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removing?.source === "upload" ? "Delete attachment" : "Delete photo"}
            </AlertDialogTitle>
            <AlertDialogDescription>{removing ? removeMessage(removing.item) : null}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />} disabled={removeBusy}>
              Cancel
            </AlertDialogClose>
            <Button color="destructive" onClick={() => void confirmRemove()} loading={removeBusy}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

function removeMessage(item: IssueMediaItem): string {
  if (item.source === "field") return `Deletes it from ${item.field.name} for everyone.`;
  if (item.source === "form") {
    const { form, submission } = item.onFile;
    return formPhotoDeleteMessage(form, submission);
  }
  if (item.source === "note" || item.source === "note_inline") {
    return "Deletes it from its comment in Activity, for everyone.";
  }
  return `${item.fileName ?? "This file"} is deleted from the Project for everyone.`;
}

function removable(item: IssueMediaItem, canEdit: boolean): boolean {
  if (item.source === "note" || item.source === "note_inline") return !!item.note.viewerCanEdit;
  return canEdit;
}

function MediaFilterMenu({
  items,
  source,
  from,
  focusLabel,
  onSource,
  onFrom,
  onClear,
}: {
  items: IssueMediaItem[];
  source: MediaSource | null;
  from: string | null;
  focusLabel?: string | undefined;
  onSource: (value: MediaSource | null) => void;
  onFrom: (value: string | null) => void;
  onClear: () => void;
}) {
  const { sources, places } = useMemo(() => {
    const bySource = new Map<MediaSource, number>();
    const byPlace = new Map<string, { label: string; count: number }>();
    for (const item of items) {
      const origin = mediaItemOrigin(item);
      bySource.set(origin, (bySource.get(origin) ?? 0) + 1);
      const place = mediaPlace(item);
      if (place) byPlace.set(place.id, { label: place.label, count: (byPlace.get(place.id)?.count ?? 0) + 1 });
    }
    return { sources: bySource, places: byPlace };
  }, [items]);
  const fromLabel = from ? places.get(from)?.label : undefined;
  const applied = focusLabel ? [focusLabel] : [source ? MEDIA_SOURCES[source].group : null, fromLabel].filter(Boolean);
  return (
    <Menu>
      <MenuTrigger
        render={
          <FilterToggle
            size="xs"
            icon="filter"
            label="Filter"
            readout={applied.length > 0 ? applied.join(" · ") : null}
            count={applied.length}
            onClear={applied.length > 0 ? onClear : undefined}
            tooltip="Filter attachments"
            clearTooltip="Clear filters"
          />
        }
      />
      <MenuPopup align="start" width="action">
        <MenuSub>
          <MenuSubTrigger size="lg">
            <Text size="sm">Source</Text>
          </MenuSubTrigger>
          <MenuSubPopup width="select" sheetTitle="Source">
            <MenuRadioGroup
              value={focusLabel ? FOCUS : (source ?? ALL)}
              onValueChange={(next) => onSource(next === ALL ? null : (next as MediaSource))}
            >
              <MenuRadioItem value={ALL} meta={items.length}>
                All
              </MenuRadioItem>
              <MenuSeparator />
              {(Object.keys(MEDIA_SOURCES) as MediaSource[]).map((key) => (
                <MenuRadioItem key={key} value={key} meta={sources.get(key) ?? 0}>
                  {MEDIA_SOURCES[key].group}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuSubPopup>
        </MenuSub>
        {places.size > 0 ? (
          <MenuSub>
            <MenuSubTrigger size="lg">
              <Text size="sm">From</Text>
            </MenuSubTrigger>
            <MenuSubPopup width="select" sheetTitle="From">
              <MenuRadioGroup
                value={focusLabel ? FOCUS : (from ?? ALL)}
                onValueChange={(next) => onFrom(next === ALL ? null : next)}
              >
                <MenuRadioItem value={ALL} meta={items.length}>
                  All
                </MenuRadioItem>
                <MenuSeparator />
                {[...places].map(([id, place]) => (
                  <MenuRadioItem key={id} value={id} meta={place.count}>
                    {place.label}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuSubPopup>
          </MenuSub>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}

function MediaTypePills({
  items,
  narrowed,
  value,
  onChange,
}: {
  items: IssueMediaItem[];
  narrowed: IssueMediaItem[];
  value: ReadonlySet<MediaKind>;
  onChange: (value: ReadonlySet<MediaKind>) => void;
}) {
  const present = new Set(items.map(kindOf));
  if (present.size < 2) return null;
  return (
    <>
      {MEDIA_KINDS.filter(([kind]) => present.has(kind)).map(([kind, label]) => {
        const on = value.has(kind);
        return (
          <Button
            key={kind}
            size="xs"
            variant="outline"
            color={on ? "primary" : "neutral"}
            aria-pressed={on}
            onClick={() => {
              const next = new Set(value);
              if (on) next.delete(kind);
              else next.add(kind);
              onChange(next);
            }}
          >
            {label}
            <Text size={null} variant="muted" tabular>
              {narrowed.filter((item) => kindOf(item) === kind).length}
            </Text>
          </Button>
        );
      })}
    </>
  );
}

const GROUP_LABELS: Record<MediaGroupBy, string> = { ...MEDIA_GROUP_BY_LABELS, all: "All" };

function MediaGroupChip({
  value,
  onChange,
}: {
  value: MediaGroupBy;
  onChange: (value: MediaGroupBy) => void;
}) {
  const changed = value !== DEFAULT_GROUP_BY;
  return (
    <Menu>
      <MenuTrigger
        render={
          <FilterToggle
            size="xs"
            icon="layers"
            label="Group"
            readout={changed ? GROUP_LABELS[value] : null}
            onClear={changed ? () => onChange(DEFAULT_GROUP_BY) : undefined}
            tooltip="Group"
            clearTooltip="Reset grouping"
          />
        }
      />
      <MenuPopup align="start" width="select">
        <MenuRadioGroup value={value} onValueChange={(next) => onChange(next as MediaGroupBy)}>
          <MenuRadioItem value={DEFAULT_GROUP_BY}>{GROUP_LABELS[DEFAULT_GROUP_BY]}</MenuRadioItem>
          <MenuSeparator />
          {(Object.keys(GROUP_LABELS) as MediaGroupBy[])
            .filter((key) => key !== DEFAULT_GROUP_BY)
            .map((key) => (
              <MenuRadioItem key={key} value={key}>
                {GROUP_LABELS[key]}
              </MenuRadioItem>
            ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}

const VIEW_MODES = [
  { mode: "list", label: "List", icon: "list" },
  { mode: "grid", label: "Grid", icon: "layout-grid" },
] as const;

function MediaViewToggle({
  mode,
  onModeChange,
}: {
  mode: "list" | "grid";
  onModeChange: (mode: "list" | "grid") => void;
}) {
  return <ViewModeToggle mode={mode} modes={VIEW_MODES} onModeChange={onModeChange} />;
}
